import React from 'react';
import { css } from 'styled-components';

import { CAMADAS } from '../../../styles/camadas';

import { anelDeFoco, estiloRotulo, styledDs as styled } from '../../../styles/theme';
import {
  DashboardFilterDef,
  DATA_MAXIMA_SQL,
  DATA_MINIMA_SQL,
  dataAplicavel,
} from '../../../infra/dashboardModel';
import { DsMultiSelect } from '../MultiSelect';

export type DsFilterControlProps = {
  filtro: DashboardFilterDef;
  value: string;
  onChange: (value: string) => void;
  onCommit?: () => void;
};

/**
 * Base comum do campo simples e do grupo multivalor: enquanto os dois tiverem
 * exatamente a mesma caixa, a barra de filtros continua alinhada sozinha.
 * `position: relative` existe para o aviso poder se ancorar no campo em vez de
 * empilhar dentro dele.
 */
const caixaDeFiltro = css`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
  font-family: ${({ theme }) => theme.font.family};

  > span {
    ${estiloRotulo}
  }
`;

const Field = styled.label`
  ${caixaDeFiltro}

  input,
  select {
    height: 32px;
    min-width: 160px;
    border-radius: ${({ theme }) => theme.radius.md};
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    padding: 0 ${({ theme }) => theme.space.sm};
    font-family: ${({ theme }) => theme.font.family};
    font-size: ${({ theme }) => theme.font.sizeSm};
  }

  input:focus-visible,
  select:focus-visible {
    ${anelDeFoco('-1px')}
  }
`;

/**
 * Região viva do campo, sempre presente e sempre fora do fluxo. São dois
 * motivos distintos:
 *
 * 1. fora do fluxo porque a barra alinha os controles pela base
 *    (`align-items: flex-end`): um aviso que ocupasse altura faria só este
 *    campo crescer e subir, desenquadrando a linha inteira — e ainda pesaria
 *    na conta da quebra de linha quando há muitos filtros;
 * 2. sempre presente porque leitor de tela só anuncia com segurança a mudança
 *    de uma região viva que já existia na página antes de mudar.
 *
 * Vazia ela não desenha nada: a moldura visível é do `AvisoTexto`.
 */
const Aviso = styled.small`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: ${CAMADAS.avisoDeFiltro};
  margin-top: ${({ theme }) => theme.space.xs};
  /* Fica por cima do que vem abaixo do cabeçalho; não pode roubar o clique. */
  pointer-events: none;
`;

/**
 * Sobreposto ao conteúdo de baixo, o texto precisa de fundo próprio para
 * continuar legível — mesma escolha do balão do informativo.
 */
const AvisoTexto = styled.span`
  display: block;
  width: max-content;
  max-width: 240px;
  padding: ${({ theme }) => theme.space.xs};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.card};
  color: ${({ theme }) => theme.colors.statusCritical};
  font-size: ${({ theme }) => theme.font.sizeXs};
  line-height: 1.35;
`;

const Grupo = styled.div`
  ${caixaDeFiltro}
`;

export const DsFilterControl: React.FC<DsFilterControlProps> = ({
  filtro,
  value,
  onChange,
  onCommit,
}) => {
  const opcoes = filtro.options ?? [];
  const tipo = filtro.tipo ?? 'lista';
  // Data pela metade não é erro do usuário, é o campo nativo avisando a cada
  // tecla — mas também não é uma data que dê para consultar.
  const dataIncompleta = tipo === 'data' && !dataAplicavel(value);

  function controle(): React.ReactNode {
    if (tipo === 'data') {
      return (
        <input
          type="date"
          aria-label={filtro.label}
          value={value}
          min={DATA_MINIMA_SQL}
          max={DATA_MAXIMA_SQL}
          aria-invalid={dataIncompleta}
          onChange={({ target }) => onChange(target.value)}
        />
      );
    }

    if (opcoes.length > 0) {
      return (
        <select
          aria-label={filtro.label}
          value={value}
          onChange={({ target }) => onChange(target.value)}
        >
          {opcoes.map((opcao) => (
            <option key={opcao.value} value={opcao.value}>
              {opcao.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={tipo === 'numero' && !filtro.multiplo ? 'number' : 'text'}
        aria-label={filtro.label}
        placeholder={filtro.multiplo ? 'separe por vírgula' : undefined}
        value={value}
        onChange={({ target }) => onChange(target.value)}
        onKeyDown={(evento) => evento.key === 'Enter' && onCommit?.()}
        onBlur={() => onCommit?.()}
      />
    );
  }

  if (opcoes.length > 0 && filtro.multiplo) {
    return (
      <Grupo>
        <span>{filtro.label}</span>
        <DsMultiSelect
          label={filtro.label}
          options={opcoes}
          values={value.split(',').filter(Boolean)}
          placeholder="Nenhuma selecionada"
          searchPlaceholder={`Buscar em ${filtro.label.toLowerCase()}...`}
          onApply={(valores) => onChange(valores.join(','))}
        />
      </Grupo>
    );
  }

  return (
    <Field>
      <span>{filtro.label}</span>
      {controle()}
      {tipo === 'data' && (
        <Aviso role="status">
          {dataIncompleta && (
            <AvisoTexto>Informe a data completa para o painel atualizar.</AvisoTexto>
          )}
        </Aviso>
      )}
    </Field>
  );
};
